import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronRight, Dumbbell, BookOpen, Music, Sparkles, Flame } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  warmup:   { Icon: Dumbbell, color: 'from-blue to-cyan-400',     accent: 'text-cyan-300',  bg: 'bg-cyan-500/10  border-cyan-500/30' },
  lesson:   { Icon: BookOpen, color: 'from-purple-500 to-pink-500', accent: 'text-purple-300', bg: 'bg-purple-500/10 border-purple-500/30' },
  song:     { Icon: Music,    color: 'from-amber to-orange-500',   accent: 'text-amber',     bg: 'bg-amber/10    border-amber/30' },
  freeplay: { Icon: Sparkles, color: 'from-green-500 to-emerald-400', accent: 'text-green-300', bg: 'bg-green-500/10 border-green-500/30' },
};

const TodaySession = ({ lang = 'it' }) => {
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [loading, setLoading] = useState(true);
  const [completingIdx, setCompletingIdx] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([
        axios.get(`${API}/session/today`),
        axios.get(`${API}/session/streak`),
      ]);
      setSession(s.data);
      setStreak(st.data);
    } catch (err) {
      console.error('TodaySession fetch error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const completeItem = async (idx) => {
    if (!session) return;
    setCompletingIdx(idx);
    try {
      const r = await axios.post(`${API}/session/${session.id}/complete-item`, { item_index: idx });
      setSession(r.data);
      if (r.data.completed_at) {
        const st = await axios.get(`${API}/session/streak`);
        setStreak(st.data);
      }
    } catch (err) {
      console.error('complete-item error', err);
    } finally {
      setCompletingIdx(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#121212] border border-white/5 rounded-2xl p-8 min-h-[280px] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-amber border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!session) return null;

  const items = session.items || [];
  const completed = new Set(session.completed_item_indices || []);
  const total = items.length;
  const doneCount = completed.size;
  const totalMinutes = items.reduce((s, i) => s + (i.duration_minutes || 0), 0);
  const allDone = !!session.completed_at;

  return (
    <div className="relative bg-[#121212] border border-amber/20 rounded-2xl p-6 sm:p-8 overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber/10 to-transparent rounded-full -translate-y-1/3 translate-x-1/3 pointer-events-none" />
      <div className="relative z-10">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <span className="text-zinc-500 uppercase tracking-widest text-xs font-bold">
              {t('session.todayLabel', { defaultValue: 'Sessione di oggi' })}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">
              {totalMinutes} {t('session.minutes', { defaultValue: 'minuti' })} · {doneCount}/{total} {t('session.done', { defaultValue: 'fatti' })}
            </h2>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30">
            <Flame className={`w-4 h-4 ${streak.current > 0 ? 'text-orange-400' : 'text-zinc-500'}`} />
            <span className={`font-mono font-bold text-sm ${streak.current > 0 ? 'text-orange-300' : 'text-zinc-400'}`}>
              {streak.current} {t('session.streakLabel', { defaultValue: 'giorni di fila' })}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {items.map((it, idx) => {
            const meta = TYPE_META[it.type] || TYPE_META.lesson;
            const Icon = meta.Icon;
            const isDone = completed.has(idx);
            const title = lang === 'it' ? (it.title_it || it.title) : (it.title || it.title_it);
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`flex items-center gap-3 sm:gap-4 p-4 rounded-xl border transition-all ${
                  isDone
                    ? 'bg-white/[0.02] border-white/5 opacity-60'
                    : meta.bg
                }`}
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5 text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-bold uppercase tracking-wider ${isDone ? 'text-zinc-500 line-through' : meta.accent}`}>
                    {t(`session.types.${it.type}`, { defaultValue: it.type })} · {it.duration_minutes} {t('session.minShort', { defaultValue: 'min' })}
                  </div>
                  <div className={`text-sm sm:text-base font-semibold truncate ${isDone ? 'text-zinc-500 line-through' : 'text-white'}`}>
                    {title}
                  </div>
                </div>
                {isDone ? (
                  <div className="w-9 h-9 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    {it.route ? (
                      <Link to={it.item_id ? `${it.route}?focus=${it.item_id}` : it.route}>
                        <Button
                          size="sm"
                          className="bg-gradient-to-r from-amber to-orange-500 text-black font-bold px-2.5 sm:px-3"
                          aria-label={t('session.start', { defaultValue: 'Inizia' })}
                        >
                          <span className="hidden sm:inline">{t('session.start', { defaultValue: 'Inizia' })}</span>
                          <ChevronRight className="w-4 h-4 sm:w-3 sm:h-3 sm:ml-1" />
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        size="sm"
                        disabled
                        className="bg-white/[0.03] text-zinc-500 border border-white/10 px-2.5 sm:px-3 text-[11px] sm:text-xs"
                      >
                        {t('session.freeplay', { defaultValue: 'Suona libero' })}
                      </Button>
                    )}
                    <button
                      onClick={() => completeItem(idx)}
                      disabled={completingIdx === idx}
                      title={t('session.markDone', { defaultValue: 'Marca fatto' })}
                      aria-label={t('session.markDone', { defaultValue: 'Marca fatto' })}
                      className="w-9 h-9 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center hover:border-green-500/50 hover:bg-green-500/10 transition-colors disabled:opacity-50"
                    >
                      {completingIdx === idx ? (
                        <div className="w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 text-zinc-400" />
                      )}
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <AnimatePresence>
          {allDone && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-amber/15 to-orange-500/15 border border-amber/40"
            >
              <Flame className="w-5 h-5 text-orange-400" />
              <div className="flex-1">
                <div className="font-heading font-bold text-amber">
                  {t('session.completedTitle', { defaultValue: 'Sessione completata!' })}
                </div>
                <div className="text-zinc-300 text-sm">
                  {t('session.completedDesc', {
                    defaultValue: 'Streak a {{n}} giorni — torna domani per non spezzarla.',
                    n: streak.current,
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TodaySession;
